import type { ReactNode } from 'react';
import {
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { Button } from '#/components/Button/Button';
import { Divider } from '#/components/Divider/Divider';
import { FloatingImageHolder } from '#/components/FloatingImageHolder/FloatingImageHolder';
import { useFormContext } from '#/components/Form/FormContext';
import { FormSection } from '#/components/Form/FormSection';
import { useFormSubmittedInterceptor } from '#/components/Form/FormSubmittedInterceptor';
import { GeneralError } from '#/components/Form/GeneralError';
import { SecondarySubmitButton } from '#/components/Form/SubmitButton/SecondarySubmitButton';
import { Icon } from '#/components/Icon/Icon';
import { BodySmall } from '#/components/Text/BodySmall';
import { H4 } from '#/components/Text/H4';
import { TabContext } from '#/containers/TabContext';
import { useDeepEffect } from '#/hooks/useDeepEffect';
import { usePrevious } from '#/hooks/usePrevious';
import { useI18n } from '#/i18n/hooks/useI18n';
import { MultiStepFormLayout } from '#/layouts/MultiStepForm/MultiStepFormLayout';
import { colors } from '#/theme/theme';
import { trackPageView } from '#/utils/analytics/track/trackPageView';
import { isDefined } from '#/utils/functional/type';

import { FormStep } from './components/FormStep';
import { MultiStepFormHeader } from './components/MultiStepFormHeader';
import { PreviousErrorsSection } from './components/PreviousErrorsSection';
import { SectionFields } from './components/SectionFields';
import { SmallHeader } from './components/SmallHeader';
import { StyledForm } from './components/StyledForm';
import { SubmitButton } from './components/SubmitButton';
import { ToggleSection } from './components/ToggleSection';
import { getFieldsToValidate } from './helpers/getFieldsToValidate';
import { getFirstStepWithError } from './helpers/getFirstStepWithError';
import { getPreviousErrorsByStep } from './helpers/getPreviousErrorsByStep';
import {
  MultiStepForm_Box_1,
  MultiStepForm_Box_2,
  MultiStepForm_Box_3,
} from './MultiStepForm.styled';
import type {
  MultiStepFormStep,
  MultiStepFormStepRow,
  MultiStepFormStepSection,
} from './MultiStepFormTypes';
import { useMultiStep } from './useMultiStep';
import { useTrackUnmappedErrors } from './useTrackUnmappedErrors';

type SidebarSection = {
  imageUrl?: string;
  body?: ReactNode;
  width?: number;
};

type Props = {
  disableConfirmTabClose?: boolean;
  forceConfirmTabClose?: boolean;
  steps: MultiStepFormStep[];
  fields: Record<string, ReactNode>;
  isDraft?: boolean;
  isSaveDraftEnabled?: boolean;
  intro?: ReactNode;
  trackingTitle?: string;
  title?: string;
  singlePage?: boolean;
  noButtons?: boolean;
  sidebar?: SidebarSection[];
  logo?: ReactNode;
  successSection?: ReactNode;
  submitText?: string;
  hasTracking?: boolean;
  hideHeader?: boolean;
  large?: boolean;
  cartDropdownButton?: ReactNode;
};

export function MultiStepForm({
  disableConfirmTabClose = false,
  forceConfirmTabClose = false,
  steps: stepsProp,
  fields,
  isDraft,
  isSaveDraftEnabled = false,
  intro,
  trackingTitle,
  title,
  singlePage,
  noButtons,
  sidebar,
  logo,
  successSection,
  submitText,
  hasTracking = false,
  hideHeader,
  large = !singlePage,
  cartDropdownButton,
}: Props) {
  const { i18n } = useI18n();

  const {
    edited,
    errors,
    submitForm,
    getValue,
    setErrors,
    submitting,
    updateValues,
  } = useFormContext();

  const { currentStep, navigateToStep } = useMultiStep();

  useTrackUnmappedErrors({
    steps: stepsProp,
    formTitle: trackingTitle ?? title,
  });

  const steps: MultiStepFormStep[] = singlePage
    ? stepsProp.reduce(
        (
          acc,
          {
            title: stepTitle,
            preTitle,
            disabled,
            beforeSection,
            glowUp,
            sections,
          },
        ) => {
          const [s] = acc;

          if (stepTitle && !(sections.length && sections[0].title)) {
            const [firstSection, ...restSections] = sections;
            const updatedFirstSection = {
              ...firstSection,
              title: stepTitle,
              preTitle,
              disabled,
              beforeSection,
              glowUp,
            };

            return [
              {
                sections: s.sections.concat([
                  updatedFirstSection,
                  ...restSections,
                ]),
              },
            ];
          }

          return [
            {
              sections: s.sections.concat(sections),
            },
          ];
        },
        [
          {
            sections: [] as MultiStepFormStepSection[],
          },
        ],
      )
    : stepsProp;
  const stepRef = useRef(null);
  const formRef = useRef(null);
  const isTabbing = useContext(TabContext) || false;
  const prevState = usePrevious({
    isTabbing,
    currentStep,
  });
  const hasSidebar = Boolean(sidebar);
  const Section = hasSidebar ? 'div' : FormSection;

  const stepEventName =
    steps[currentStep]?.eventName ?? `Multi-Step Form: Step ${currentStep}`;
  const stepEventProperties = steps[currentStep]?.eventProperties ?? {};

  const previousErrorsByStep: string[][] = errors
    ? getPreviousErrorsByStep({
        steps,
        currentStep,
        errors,
      })
    : [];
  const previousErrors = previousErrorsByStep.flat();

  useEffect(() => {
    if (
      submitting ||
      (singlePage && !forceConfirmTabClose) ||
      !edited ||
      disableConfirmTabClose
    )
      return;

    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [
    disableConfirmTabClose,
    forceConfirmTabClose,
    edited,
    singlePage,
    submitting,
  ]);

  // Prevent refiring the same event twice
  const lastTrackedRef = useRef<{ step: number; eventName: string } | null>(
    null,
  );

  useDeepEffect(() => {
    if (!hasTracking) return;

    const lastTracked = lastTrackedRef.current;
    if (
      lastTracked?.step === currentStep &&
      lastTracked.eventName === stepEventName
    )
      return;

    lastTrackedRef.current = { step: currentStep, eventName: stepEventName };
    trackPageView(stepEventName, {
      step_index: currentStep,
      ...stepEventProperties,
    });
  }, [currentStep, hasTracking, stepEventName, stepEventProperties]);

  useEffect(() => {
    if (stepRef?.current) {
      // @ts-expect-error TS(2315): Type 'NodeList' is not generic.
      // TODO: Fix this the next time the file is edited.
      // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const inputsList: NodeList<HTMLElement> =
        // @ts-expect-error TS(2339): Property 'querySelectorAll' does not exist on type... Remove this comment to see the full error message
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-call
        stepRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]',
        );
      // set tabindex=0 for all input elements except those with data-skip-tab or data-no-tab set to "true"
      const inputs = Array.prototype.slice.call(inputsList).filter((input) => {
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const noTabAttr = input.getAttribute('data-no-tab');
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const skipTabAttr = input.getAttribute('data-skip-tab');
        return (
          (!noTabAttr || noTabAttr !== 'true') &&
          (!skipTabAttr || skipTabAttr !== 'true')
        );
      });

      inputs.forEach((el: HTMLElement) => {
        // oxlint-disable-next-line no-param-reassign
        el.tabIndex = 0;
      });

      if (isTabbing && prevState && currentStep !== prevState.currentStep) {
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        inputs[0].focus();
      }
    }
  }, [currentStep, isTabbing, prevState]);

  const mappedHeaderSteps = steps.map((step) => ({
    title: step.title,
    ignoreInNav: step.ignoreInNav,
  }));

  const seenFields = new Map<string, number>();

  const submittedInterceptor = () => {
    if (!isDraft || getValue('hasNextStep')) return;

    const firstStepWithError = getFirstStepWithError(steps, errors);
    if (firstStepWithError !== null) {
      setErrors(
        {
          general: i18n(
            'Please complete all required fields in order to save this draft.',
          ),
        },
        true,
      );
      if (firstStepWithError === currentStep) return;

      navigateToStep(firstStepWithError);
    }
  };

  useFormSubmittedInterceptor({
    onIntercept: submittedInterceptor,
  });

  const submitDraft = useCallback(() => {
    return submitForm({
      publish: false,
      hasNextStep: false,
      validate: getFieldsToValidate(steps, currentStep),
    });
  }, [currentStep, steps, submitForm]);

  const submitStep = useCallback(
    (stepIndex: number) => {
      return updateValues({
        publish: false,
        hasNextStep: true,
        validate: getFieldsToValidate(steps, stepIndex),
      });
    },
    [steps, updateValues],
  );

  const submitFormWithPublish = useCallback(
    (stepIndex: number) => {
      return updateValues({
        publish: true,
        hasNextStep: false,
        validate: getFieldsToValidate(steps, stepIndex),
      });
    },
    [steps, updateValues],
  );

  return (
    <MultiStepFormLayout
      sidebar={
        sidebar && (
          <>
            {sidebar.map((section, index) =>
              index === currentStep ? (
                <MultiStepForm_Box_3
                  key={
                    section.imageUrl ??
                    (typeof section.width === 'number'
                      ? `w-${section.width}`
                      : `sidebar-${index}`)
                  }
                >
                  <FloatingImageHolder
                    style={{
                      width: section.width,
                      maxWidth: '100%',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}
                  >
                    <img src={section.imageUrl} alt="" />
                  </FloatingImageHolder>

                  {section.body && (
                    <BodySmall
                      style={{
                        maxWidth: 400,
                        marginTop: 36,
                        color: colors.lightContentGrey,
                      }}
                    >
                      {section.body}
                    </BodySmall>
                  )}
                </MultiStepForm_Box_3>
              ) : null,
            )}
          </>
        )
      }
    >
      <div ref={formRef}>
        <StyledForm>
          {!hideHeader && (
            <div
              style={{
                marginBottom: hasSidebar ? 36 : 0,
                background: 'white',
                position: 'relative',
                zIndex: 2,
              }}
            >
              {hasSidebar ? (
                <SmallHeader steps={mappedHeaderSteps} logo={logo} />
              ) : (
                <MultiStepFormHeader
                  steps={mappedHeaderSteps}
                  submitDraft={submitDraft}
                  errors={previousErrorsByStep}
                  trackingTitle={trackingTitle}
                  title={title}
                  edited={edited}
                  singlePage={singlePage}
                  isDraft={isDraft}
                  cartDropdownButton={cartDropdownButton}
                />
              )}
            </div>
          )}
          <div style={{ position: 'relative', flex: '1 1 auto' }}>
            {successSection ??
              steps.map((step, stepIndex) =>
                singlePage || stepIndex === currentStep ? (
                  <FormStep
                    $active={singlePage || currentStep === stepIndex}
                    key={step.title ?? stepIndex}
                    style={{ background: hasSidebar ? 'white' : undefined }}
                  >
                    {step.loadingSection ?? (
                      <div
                        ref={
                          singlePage || currentStep === stepIndex
                            ? stepRef
                            : null
                        }
                      >
                        {isDefined(intro) && step.showIntro ? intro : null}
                        {previousErrors.length > 0 && (
                          <PreviousErrorsSection large>
                            <H4>{i18n('Previous Errors')}</H4>

                            <BodySmall style={{ marginBottom: 40 }}>
                              {i18n(
                                'We found these errors on previous pages. You may fix them here, or go back to make further changes.',
                              )}
                            </BodySmall>

                            <>{previousErrors.map((error) => fields[error])}</>
                          </PreviousErrorsSection>
                        )}

                        <GeneralError
                          ignore={step.sections.map(
                            ({ matchError }) => matchError,
                          )}
                        />

                        {step.sections.map((section, sectionIndex) => {
                          let dividerCount = 0;
                          const lastStep = stepIndex === steps.length - 1;

                          return (
                            <Section
                              key={`${stepIndex}-${sectionIndex}`}
                              preTitle={section.preTitle}
                              disabled={section.disabled}
                              beforeSection={section.beforeSection}
                              glowUp={section.glowUp}
                              title={hasSidebar ? undefined : section.title}
                              // oxlint-disable-next-line react/jsx-props-no-spreading
                              {...(hasSidebar
                                ? undefined
                                : {
                                    large,
                                    description: section.intro,
                                    compactDescription:
                                      section?.introOptions?.compact,
                                    bodyPaddingBottom: noButtons
                                      ? ['4px', null, 34]
                                      : undefined,
                                  })}
                            >
                              {section.actions && (
                                <div style={{ position: 'relative' }}>
                                  <MultiStepForm_Box_2>
                                    {section.actions.map((action, index) => (
                                      <div
                                        key={
                                          isValidElement(action) &&
                                          action.key != null
                                            ? String(action.key)
                                            : `action-${index}`
                                        }
                                        style={{ marginLeft: 12 }}
                                      >
                                        {action}
                                      </div>
                                    ))}
                                  </MultiStepForm_Box_2>
                                </div>
                              )}

                              {hasSidebar && (
                                <>
                                  <H4>{section.title}</H4>

                                  {section.intro && (
                                    <div style={{ marginBottom: 24 }}>
                                      {section.intro}
                                    </div>
                                  )}
                                </>
                              )}

                              {section.matchError && (
                                <GeneralError match={section.matchError} />
                              )}

                              {section.rows.map(
                                (
                                  row: MultiStepFormStepRow | null | undefined,
                                ) =>
                                  row ? (
                                    <ToggleSection
                                      title={row.toggle}
                                      key={row.toggle ?? row.fields.join('-')}
                                      toggleQaId={row.toggleQaId}
                                    >
                                      <SectionFields
                                        row={row}
                                        fields={fields}
                                        seenFields={seenFields}
                                      />
                                    </ToggleSection>
                                  ) : (
                                    (() => {
                                      dividerCount += 1;
                                      return (
                                        <Divider
                                          key={`divider-${section.title ?? ''}-${dividerCount}`}
                                          marginTop={54}
                                          marginBottom={54}
                                        />
                                      );
                                    })()
                                  ),
                              )}

                              {sectionIndex === step.sections.length - 1 &&
                                stepIndex === currentStep &&
                                !noButtons && (
                                  <MultiStepForm_Box_1>
                                    {stepIndex > 0 && (
                                      <div
                                        style={{
                                          padding: '8px',
                                          marginRight: 'auto',
                                        }}
                                      >
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          icon={{ name: 'arrow-left' }}
                                          size="large"
                                          onClick={() => {
                                            navigateToStep(stepIndex - 1);
                                          }}
                                          qaId="multi-form-prev-step"
                                          title={i18n('Back')}
                                        />
                                      </div>
                                    )}
                                    {lastStep && isSaveDraftEnabled ? (
                                      <div style={{ padding: '8px' }}>
                                        <SecondarySubmitButton
                                          qaId="save-draft"
                                          type="button"
                                          size="large"
                                          // TODO: Fix this the next time the file is edited.
                                          // oxlint-disable-next-line @typescript-eslint/no-misused-promises
                                          onClick={submitDraft}
                                        >
                                          {i18n('Save Draft')}
                                        </SecondarySubmitButton>
                                      </div>
                                    ) : null}
                                    {step.leftButtons && (
                                      <div style={{ padding: '8px' }}>
                                        {step.leftButtons}
                                      </div>
                                    )}

                                    {(() => {
                                      if (lastStep) {
                                        return (
                                          <div style={{ padding: '8px' }}>
                                            <SubmitButton
                                              size="large"
                                              onClick={() =>
                                                submitFormWithPublish(stepIndex)
                                              }
                                            >
                                              {step.nextText ??
                                                submitText ??
                                                i18n('Finish & Publish')}
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  marginLeft: '4px',
                                                }}
                                              >
                                                <Icon
                                                  name="arrow-right"
                                                  size={18}
                                                />
                                              </div>
                                            </SubmitButton>
                                          </div>
                                        );
                                      }

                                      if (!step.hideNextStep) {
                                        return (
                                          <div style={{ padding: '8px' }}>
                                            <SubmitButton
                                              disabled={step.submitIsDisabled}
                                              size="large"
                                              onClick={() =>
                                                submitStep(stepIndex)
                                              }
                                              qaId="multi-form-next-step"
                                            >
                                              {step.nextText ?? i18n('Next')}
                                              <div
                                                style={{
                                                  marginLeft: '4px',
                                                  display: 'inline-block',
                                                }}
                                              >
                                                <Icon
                                                  name="arrow-right"
                                                  size={18}
                                                />
                                              </div>
                                            </SubmitButton>
                                          </div>
                                        );
                                      }

                                      return null;
                                    })()}
                                  </MultiStepForm_Box_1>
                                )}
                            </Section>
                          );
                        })}
                      </div>
                    )}
                  </FormStep>
                ) : null,
              )}
          </div>
          {steps[currentStep]?.footer ? steps[currentStep].footer : null}
        </StyledForm>
      </div>
    </MultiStepFormLayout>
  );
}
